/* Reef Lagoon — Tee Carousel.
 * Progressive enhancement for sections/reef-tee-carousel.liquid:
 *   - desktop prev/next arrows (shown only when there's overflow on a hover device)
 *   - click-drag to scroll (desktop)
 *   - touch: the centred card reveals its lifestyle shot (no hover dependency)
 *   - gentle scroll-in fade-up of the cards
 *   - opt-in autoplay (off by default; pauses on interaction)
 * All motion is gated behind prefers-reduced-motion. Idempotent; re-inits on
 * shopify:section:load (theme editor). The flat->lifestyle cross-fade itself is CSS. */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var NO_HOVER = window.matchMedia && window.matchMedia('(hover: none)').matches;
  var HAS_HOVER = window.matchMedia && window.matchMedia('(hover: hover)').matches;

  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  function initCarousel(root) {
    if (!root || root.dataset.rtcInit === '1') return;
    root.dataset.rtcInit = '1';

    var track = root.querySelector('[data-rtc-track]');
    var viewport = root.querySelector('[data-rtc-viewport]');
    if (!track) return;
    var cards = [].slice.call(track.querySelectorAll('[data-rtc-card]'));
    if (!cards.length) return;

    var prev = root.querySelector('[data-rtc-prev]');
    var next = root.querySelector('[data-rtc-next]');

    /* ---- scroll-in fade-up (cards) ---- */
    if (!REDUCE) {
      root.classList.add('rtc--observe');
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            var idx = cards.indexOf(e.target);
            window.setTimeout(function () { e.target.classList.add('is-in'); }, (idx < 0 ? 0 : idx % 6) * 80);
            io.unobserve(e.target);
          });
        }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
        cards.forEach(function (c) { io.observe(c); });
      } else {
        cards.forEach(function (c) { c.classList.add('is-in'); });
      }
    }

    /* ---- arrows (desktop overflow only) ---- */
    function hasOverflow() { return track.scrollWidth - track.clientWidth > 4; }

    function updateArrows() {
      if (!prev || !next) return;
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    }

    function scrollByPage(dir) {
      var amount = Math.max(track.clientWidth * 0.85, 240);
      track.scrollBy({ left: dir * amount, behavior: REDUCE ? 'auto' : 'smooth' });
    }

    function syncControls() {
      if (viewport) viewport.classList.toggle('is-controls', HAS_HOVER && hasOverflow());
      updateArrows();
    }

    if (prev) prev.addEventListener('click', function () { scrollByPage(-1); });
    if (next) next.addEventListener('click', function () { scrollByPage(1); });

    /* ---- touch: mark the centred card active (reveals its lifestyle shot) ---- */
    var activeCard = null;
    function updateActive() {
      if (!NO_HOVER) return;
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = null, bestDist = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i];
        var center = c.offsetLeft + c.offsetWidth / 2;
        var d = Math.abs(center - mid);
        if (d < bestDist) { bestDist = d; best = c; }
      }
      if (best && best !== activeCard) {
        if (activeCard) activeCard.classList.remove('is-active');
        best.classList.add('is-active');
        activeCard = best;
      }
    }

    /* ---- click-drag to scroll (hover/fine pointer only) ---- */
    if (HAS_HOVER) {
      var down = false, startX = 0, startLeft = 0, moved = false;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch' || e.button !== 0) return;
        down = true; moved = false;
        startX = e.clientX; startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startLeft - dx;
      });
      function endDrag() {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('pointerleave', endDrag);
      // Suppress the click that follows a real drag so it doesn't navigate.
      track.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    }

    /* ---- scroll bookkeeping (rAF-throttled) ---- */
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        updateArrows();
        updateActive();
        ticking = false;
      });
    }, { passive: true });

    window.addEventListener('resize', syncControls);

    /* ---- autoplay (opt-in, never under reduced-motion or touch) ---- */
    if (root.dataset.rtcAutoplay === 'true' && !REDUCE && HAS_HOVER) {
      var interval = clamp(parseInt(root.dataset.rtcInterval, 10) || 5, 3, 10) * 1000;
      var timer = null;
      function tick() {
        var max = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft >= max - 2) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollByPage(1);
        }
      }
      function play() { if (!timer && hasOverflow()) timer = window.setInterval(tick, interval); }
      function pause() { if (timer) { window.clearInterval(timer); timer = null; } }
      root.addEventListener('pointerenter', pause);
      root.addEventListener('pointerleave', play);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', play);
      document.addEventListener('visibilitychange', function () { document.hidden ? pause() : play(); });
      play();
    }

    // First paint.
    syncControls();
    updateActive();
  }

  function initAll() {
    var els = document.querySelectorAll('[data-rtc]');
    for (var i = 0; i < els.length; i++) initCarousel(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-rtc]') : null;
    if (el) { initCarousel(el); } else { initAll(); }
  });
})();
